// wwwroot/js/permission-visualizer.js
// Pure ES6 module – clean imports, no global scripts

import cytoscape from "cytoscape";
import dagre from "cytoscape-dagre";

// Register dagre layout extension (runs automatically once when module loads)
cytoscape.use(dagre);

export const PermissionVisualizer = {
    cy: null,
    exprCounter: 0,
    themeObserver: null,
    resizeObserver: null,

    updateTheme() {
        if (!this.cy) {
            return;
        }

        const theme = this._getTheme();

        this.cy.style().fromJson(this._getStyles(theme)).update();
    },

    _getTheme() {
        const isDark = document.documentElement.classList.contains("dark");
        const fallback = isDark
            ? {
                  background: "#1d1d26",
                  surface: "#252534",
                  panel: "#21212d",
                  foreground: "#f4f4fa",
                  muted: "#a5b4d4",
                  line: "#5f6495",
                  accent: "#8ed9f8",
                  danger: "#f47777",
                  success: "#5bd68e",
                  warning: "#f1c46a",
                  info: "#73bff2",
              }
            : {
                  background: "#f7f8fb",
                  surface: "#ffffff",
                  panel: "#f2f3f8",
                  foreground: "#1c1c25",
                  muted: "#53627e",
                  line: "#7782ad",
                  accent: "#4f5ea8",
                  danger: "#d13f4c",
                  success: "#268653",
                  warning: "#ad7215",
                  info: "#277eb5",
              };
        const probe = document.createElement("span");
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d", { willReadFrequently: true });

        probe.style.position = "fixed";
        probe.style.width = "0";
        probe.style.height = "0";
        probe.style.opacity = "0";
        probe.style.pointerEvents = "none";

        canvas.width = 1;
        canvas.height = 1;

        document.body.appendChild(probe);

        const resolveToken = (name, fallbackColor) => {
            if (!context) {
                return fallbackColor;
            }

            probe.style.color = `var(${name}, ${fallbackColor})`;
            const resolvedColor = window.getComputedStyle(probe).color;

            context.clearRect(0, 0, 1, 1);
            context.fillStyle = fallbackColor;
            context.fillStyle = resolvedColor;
            context.fillRect(0, 0, 1, 1);

            const [red, green, blue, alpha] = context.getImageData(
                0,
                0,
                1,
                1,
            ).data;

            if (alpha === 255) {
                return `rgb(${red}, ${green}, ${blue})`;
            }

            return `rgba(${red}, ${green}, ${blue}, ${(alpha / 255).toFixed(
                3,
            )})`;
        };

        const theme = {
            background: resolveToken("--bc-bg", fallback.background),
            surface: resolveToken("--bc-surface", fallback.surface),
            panel: resolveToken("--bc-panel", fallback.panel),
            foreground: resolveToken("--bc-fg", fallback.foreground),
            muted: resolveToken("--bc-muted", fallback.muted),
            line: resolveToken("--bc-line", fallback.line),
            accent: resolveToken("--bc-accent", fallback.accent),
            danger: resolveToken("--bc-danger", fallback.danger),
            success: resolveToken("--bc-success", fallback.success),
            warning: resolveToken("--bc-warning", fallback.warning),
            info: resolveToken("--bc-info", fallback.info),
            fontFamily:
                window.getComputedStyle(document.body).fontFamily ||
                "system-ui, sans-serif",
        };

        probe.remove();

        return theme;
    },

    _getStyles(theme) {
        return [
            {
                selector: "core",
                style: {
                    "active-bg-color": theme.accent,
                    "active-bg-opacity": 0.12,
                    "active-bg-size": 28,
                    "selection-box-color": theme.accent,
                    "selection-box-opacity": 0.08,
                    "selection-box-border-color": theme.accent,
                    "selection-box-border-width": 1,
                },
            },
            {
                selector: "node",
                style: {
                    label: "data(label)",
                    shape: "round-rectangle",
                    width: "data(width)",
                    height: 24,
                    padding: "12px",
                    "background-color": theme.panel,
                    "background-opacity": 1,
                    "border-width": 1,
                    "border-color": theme.line,
                    "border-opacity": 0.72,
                    color: theme.foreground,
                    "font-family": theme.fontFamily,
                    "font-size": "12px",
                    "font-weight": "600",
                    "text-valign": "center",
                    "text-halign": "center",
                    "text-wrap": "ellipsis",
                    "text-max-width": "210px",
                    "min-zoomed-font-size": 7,
                    "overlay-opacity": 0,
                    "underlay-color": theme.line,
                    "underlay-opacity": 0.1,
                    "underlay-padding": 3,
                    "underlay-shape": "round-rectangle",
                    "z-index": 2,
                },
            },
            {
                selector: ".user",
                style: {
                    "border-color": theme.info,
                    "underlay-color": theme.info,
                },
            },
            {
                selector: ".group",
                style: {
                    "border-color": theme.success,
                    "underlay-color": theme.success,
                },
            },
            {
                selector: ".index",
                style: {
                    "border-color": theme.accent,
                    "underlay-color": theme.accent,
                },
            },
            {
                selector: ".rule",
                style: {
                    "border-color": theme.warning,
                    color: theme.warning,
                    "underlay-color": theme.warning,
                    "font-size": "10px",
                    "font-weight": 700,
                },
            },
            {
                selector: ".op-and, .op-or, .op-not",
                style: {
                    width: 42,
                    height: 42,
                    padding: 0,
                    shape: "ellipse",
                    "background-color": theme.surface,
                    "border-color": theme.line,
                    "border-width": 1.5,
                    color: theme.muted,
                    "font-size": "9px",
                    "font-weight": 700,
                    "underlay-opacity": 0.06,
                },
            },
            {
                selector: ".op-not",
                style: {
                    shape: "diamond",
                    width: 46,
                    height: 46,
                },
            },
            {
                selector: ".super, .super-node",
                style: {
                    "border-color": theme.danger,
                    "border-width": 2,
                    color: theme.danger,
                    "underlay-color": theme.danger,
                    "underlay-opacity": 0.14,
                    "font-weight": 700,
                },
            },
            {
                selector: ".super-node",
                style: {
                    "font-size": "12px",
                    padding: "14px",
                },
            },
            {
                selector: ".denied",
                style: {
                    "background-color": theme.danger,
                    "background-opacity": 0.13,
                    "border-color": theme.danger,
                    "border-style": "dashed",
                    color: theme.danger,
                    "underlay-color": theme.danger,
                },
            },
            {
                selector: ".legend",
                style: {
                    height: 18,
                    padding: "8px",
                    "font-size": "9px",
                    "font-weight": "600",
                    opacity: 0.82,
                    "underlay-opacity": 0,
                },
            },
            {
                selector: "node.is-hovered",
                style: {
                    "border-width": 2,
                    "underlay-opacity": 0.2,
                    "underlay-padding": 6,
                    "z-index": 20,
                },
            },
            {
                selector: "node:selected",
                style: {
                    "background-color": theme.surface,
                    "border-color": theme.accent,
                    "border-width": 2.5,
                    "underlay-color": theme.accent,
                    "underlay-opacity": 0.24,
                    "underlay-padding": 8,
                    "z-index": 30,
                },
            },
            {
                selector: "edge",
                style: {
                    "curve-style": "bezier",
                    "line-cap": "round",
                    "line-color": theme.line,
                    "line-opacity": 0.7,
                    width: 1.5,
                    "target-arrow-shape": "triangle",
                    "target-arrow-color": theme.line,
                    "arrow-scale": 0.72,
                    "overlay-opacity": 0,
                    "z-index": 1,
                },
            },
            {
                selector: "edge[label]",
                style: {
                    label: "data(label)",
                    color: theme.muted,
                    "font-family": theme.fontFamily,
                    "font-size": "9px",
                    "font-weight": "600",
                    "text-rotation": "none",
                    "text-margin-y": -10,
                    "text-background-color": theme.panel,
                    "text-background-opacity": 0.94,
                    "text-background-padding": 4,
                    "text-background-shape": "round-rectangle",
                    "text-border-color": theme.line,
                    "text-border-opacity": 0.35,
                    "text-border-width": 1,
                },
            },
            {
                selector: ".grant-edge",
                style: {
                    "line-color": theme.success,
                    "target-arrow-color": theme.success,
                    width: 2,
                },
            },
            {
                selector: ".condition-edge",
                style: {
                    "line-color": theme.info,
                    "target-arrow-color": theme.info,
                },
            },
            {
                selector: ".exclude-edge",
                style: {
                    "line-color": theme.danger,
                    "target-arrow-color": theme.danger,
                    "line-style": "dashed",
                },
            },
            {
                selector: ".logic-edge",
                style: {
                    "line-color": theme.line,
                    "line-opacity": 0.48,
                    "target-arrow-shape": "none",
                },
            },
            {
                selector: ".super-edge",
                style: {
                    "line-color": theme.danger,
                    "target-arrow-color": theme.danger,
                    width: 2.5,
                    "target-arrow-shape": "chevron",
                },
            },
            {
                selector: "edge.is-hovered, edge:selected",
                style: {
                    "line-opacity": 1,
                    width: 2.5,
                    "z-index": 10,
                },
            },
        ];
    },

    _getLayout(animate) {
        const reduceMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)",
        ).matches;

        return {
            name: "dagre",
            rankDir: "LR",
            ranker: "network-simplex",
            nodeSep: 72,
            rankSep: 132,
            edgeSep: 28,
            padding: 52,
            fit: true,
            animate: animate && !reduceMotion,
            animationDuration: 360,
            animationEasing: "ease-out",
        };
    },

    _getNodeWidth(label) {
        return Math.min(Math.max(84, Math.ceil(label.length * 7.2)), 220);
    },

    _addLegend() {
        if (!this.cy) {
            return;
        }

        const legendData = [
            { id: "l_super", label: "Global Access", class: "super-node" },
            { id: "l_index", label: "Index / Target", class: "index" },
            { id: "l_rule", label: "Grant Rule", class: "rule" },
        ];

        legendData.forEach((item, i) => {
            this.cy.add({
                group: "nodes",
                data: {
                    id: item.id,
                    label: item.label,
                    width: this._getNodeWidth(item.label),
                },
                classes: `${item.class} legend`,
                position: { x: 94, y: 40 + i * 54 },
                grabbable: false,
                locked: true,
                selectable: false,
            });
        });

        this.cy.zoom(1);
        this.cy.pan({ x: 20, y: 16 });
    },

    init(containerId) {
        const container = document.getElementById(containerId);

        if (!container) {
            throw new Error(`Container #${containerId} not found`);
        }

        this.themeObserver?.disconnect();
        this.resizeObserver?.disconnect();
        this.cy?.destroy();

        const theme = this._getTheme();

        this.cy = cytoscape({
            container,
            layout: { name: "preset" },
            style: this._getStyles(theme),
            minZoom: 0.25,
            maxZoom: 2.5,
            selectionType: "single",
            boxSelectionEnabled: false,
        });

        this.cy.on("mouseover", "node", (event) => {
            event.target.addClass("is-hovered");
            event.target.connectedEdges().addClass("is-hovered");
            container.style.cursor = "grab";
        });

        this.cy.on("mouseout", "node", (event) => {
            event.target.removeClass("is-hovered");
            event.target.connectedEdges().removeClass("is-hovered");
            container.style.cursor = "default";
        });

        this.cy.on("grab", "node", () => {
            container.style.cursor = "grabbing";
        });

        this.cy.on("free", "node", () => {
            container.style.cursor = "grab";
        });

        this.cy.on("mouseover", "edge", (event) => {
            event.target.addClass("is-hovered");
        });

        this.cy.on("mouseout", "edge", (event) => {
            event.target.removeClass("is-hovered");
        });

        this._addLegend();

        this.themeObserver = new MutationObserver(() => this.updateTheme());
        this.themeObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["class"],
        });

        this.resizeObserver = new ResizeObserver(() => this.cy?.resize());
        this.resizeObserver.observe(container);
    },

    render(astJson) {
        if (!this.cy) {
            return;
        }

        const ast = typeof astJson === "string" ? JSON.parse(astJson) : astJson;

        this.exprCounter = 0;
        const elements = this._buildElements(ast);

        this.cy.startBatch();
        this.cy.elements().remove();
        this.cy.add(elements);
        this.cy.endBatch();
        this.cy.resize();

        if (elements.length === 0) {
            this._addLegend();

            return;
        }

        this.cy.layout(this._getLayout(true)).run();
    },

    _buildElements(ast) {
        const elements = [];
        const seen = new Set();
        let nodePositionCounter = 0;

        const addNode = (id, label, classes) => {
            if (seen.has(id)) return id;
            seen.add(id);
            elements.push({
                data: { id, label, width: this._getNodeWidth(label) },
                classes,
                position: {
                    x: 120 + (nodePositionCounter % 4) * 300,
                    y: 80 + Math.floor(nodePositionCounter / 4) * 104
                }
            });
            nodePositionCounter++;
            return id;
        };

        // We only add this if there are actually any super users or groups
        const hasSupers = (ast.UserBlock?.SuperUsers?.length > 0) ||
            (ast.GroupBlock?.SuperGroups?.length > 0);

        if (hasSupers) {
            addNode('global_super', 'Unrestricted Access', 'super-node');
        }

        const addPrincipal = (name, isUser, isSuper, isDenied) => {
            //const id = isUser ? `u_${name}` : `g_${name}`;
            //let cls = isUser ? 'user' : 'group';
            //if (isSuper) cls += ' super';
            //if (isDenied) cls += ' denied';
            //addNode(id, isUser ? `@user:${name}` : `@group:${name}`, cls);
            //return id;

            const id = isUser ? `u_${name}` : `g_${name}`;
            let cls = isUser ? 'user' : 'group';

            // Use the name directly, but change the label if it's a wildcard
            const label = isUser
                ? (name === '*' ? '@user:* (Public)' : `@user:${name}`)
                : (name === '*' ? '@group:* (Any Group)' : `@group:${name}`);

            if (isSuper) cls += ' super';
            if (isDenied) cls += ' denied';

            addNode(id, label, cls);

            // Connect to unrestricted node if super
            if (isSuper && hasSupers) {
                elements.push({
                    data: { source: id, target: 'global_super', label: 'SUPER' },
                    classes: 'super-edge'
                });
            }

            return id;
        };

        // Principals
        (ast.UserBlock?.SuperUsers || []).forEach(u => addPrincipal(u, true, true, false));
        (ast.UserBlock?.DeniedUsers || []).forEach(u => addPrincipal(u, true, false, true));
        (ast.GroupBlock?.SuperGroups || []).forEach(g => addPrincipal(g, false, true, false));
        (ast.GroupBlock?.DeniedGroups || []).forEach(g => addPrincipal(g, false, false, true));

        // Indices
        const indices = new Set();
        (ast.IndexBlock?.GrantRules || []).forEach(r =>
            (r.TargetIndices || []).forEach(i => indices.add(i))
        );

        indices.forEach(ulid => {
            const short = ulid.length > 12 ? ulid.substring(0, 8) + '…' : ulid;
            addNode(`i_${ulid}`, `📄 ${short}`, 'index');
        });

        // Expression builder
        const buildExpr = (expr, prefix) => {
            if (!expr) return null;
            const kind = expr.Kind;

            if (kind === 'Reference') {
                const s = expr.Subject;

                if (!s || !s.Kind) return null;

                const isUser = s.Kind === 'User';
                const name = isUser ? s.UserName : s.GroupName;
                const id = isUser ? `u_${name}` : `g_${name}`;

                if (!name) return null; // Handle cases where name might be null/empty during typing

                // Ensure the node exists (especially for wildcards)
                if (!seen.has(id)) {
                    addPrincipal(name, isUser, false, false);
                }

                return isUser ? `u_${name}` : `g_${name}`;
            }

            if (kind === 'List') {
                const id = `${prefix}_orlist_${this.exprCounter++}`;
                addNode(id, 'OR', 'op-or');
                (expr.Subjects || []).forEach(s => {
                    const sid = buildExpr({ Kind: 'Reference', Subject: s }, prefix);
                    if (sid) elements.push({ data: { source: sid, target: id }, classes: 'logic-edge' });
                });
                return id;
            }

            if (kind === 'And') {
                const id = `${prefix}_and_${this.exprCounter++}`;
                addNode(id, 'AND', 'op-and');
                const l = buildExpr(expr.Left, prefix);
                const r = buildExpr(expr.Right, prefix);
                if (l) elements.push({ data: { source: l, target: id }, classes: 'logic-edge' });
                if (r) elements.push({ data: { source: r, target: id }, classes: 'logic-edge' });
                return id;
            }

            if (kind === 'Or') {
                const id = `${prefix}_or_${this.exprCounter++}`;
                addNode(id, 'OR', 'op-or');
                const l = buildExpr(expr.Left, prefix);
                const r = buildExpr(expr.Right, prefix);
                if (l) elements.push({ data: { source: l, target: id }, classes: 'logic-edge' });
                if (r) elements.push({ data: { source: r, target: id }, classes: 'logic-edge' });
                return id;
            }

            //if (kind === 'Not') {
            //    const id = `${prefix}_not_${this.exprCounter++}`;
            //    addNode(id, 'NOT', 'op-not');
            //    const inner = buildExpr(expr.Expression, prefix);
            //    if (inner) elements.push({ data: { source: id, target: inner }, classes: 'logic-edge' });
            //    return id;
            //}

            if (kind === 'Not') {
                const id = `${prefix}_not_${this.exprCounter++}`;
                addNode(id, 'NOT', 'op-not'); // Diamond shape
                const inner = buildExpr(expr.Expression, prefix);
                if (inner) elements.push({ data: { source: inner, target: id }, classes: 'logic-edge' });
                return id;
            }

            return null;
        };

        // Grant rules
        let ruleIdx = 0;
        (ast.IndexBlock?.GrantRules || []).forEach(rule => {
            const ruleId = `r_${ruleIdx++}`;
            addNode(ruleId, `GRANT RULE #${ruleIdx}`, 'rule');

            (rule.TargetIndices || []).forEach(ulid => {
                elements.push({
                    data: { source: ruleId, target: `i_${ulid}`, label: 'grants' },
                    classes: 'grant-edge'
                });
            });

            if (rule.PositiveExpression) {
                const posRoot = buildExpr(rule.PositiveExpression, `p${ruleIdx}`);
                if (posRoot) {
                    elements.push({
                        data: { source: posRoot, target: ruleId, label: 'IF' },
                        classes: 'condition-edge'
                    });
                }
            }

            // FIX: Handle the Exclusion Expression (The 'not' part of a rule)
            if (rule.ExclusionExpression) {
                // Build the excluded expression graph
                const exclRoot = buildExpr(rule.ExclusionExpression, `e${ruleIdx}`);
                if (exclRoot) {
                    elements.push({
                        data: { source: exclRoot, target: ruleId, label: 'EXCLUDE' }, // Changed label for clarity
                        classes: 'exclude-edge' // This applies the dashed red line
                    });
                }
            }

            //if (rule.ExclusionExpression) {
            //    const exclRoot = buildExpr(rule.ExclusionExpression, `e${ruleIdx}`);
            //    if (exclRoot) {
            //        elements.push({
            //            data: { source: exclRoot, target: ruleId, label: 'NOT' },
            //            classes: 'exclude-edge'
            //        });
            //    }
            //}
        });

        return elements;
    },

    clear() {
        if (this.cy) {
            this.cy.elements().remove();
        }
    },

    downloadGraph() {
        if (!this.cy) {
            return;
        }

        const theme = this._getTheme();
        const pngContent = this.cy.png({
            full: true,
            scale: 2,
            bg: theme.background,
        });
        const link = document.createElement("a");
        link.download = `marumbi-security-graph-${new Date().getTime()}.png`;
        link.href = pngContent;
        link.click();
    },
};
